/**
 * 속성 관리자 (spec 2026-09-18-calendar-properties §9): 허브 `#calpPropMgr` 의 코드를
 * project-list.js 에서 빼 온 것 + 유형별 설정 칸(상태 그룹 · 관계 대상/짝/하나만 ·
 * 롤업 관계→속성→함수 · 자동 번호 접두) + 오버레이의 프로젝트 속성 읽기 전용 절.
 * 데이터는 `#calPropsData` 섬(서버가 그린다), 훅 이름은 허브의 것 그대로(e2e 가 잡고 있다).
 * 문서 위임이라 SPA 교체 뒤에도 다시 묶지 않는다; 섬은 열 때마다 다시 읽는다.
 */
import { showToast } from './calendar-history.js';
import { appConfirm } from './confirm-dialog.js';
import { initInappSelects, setOptionIcons } from './inapp-select.js';
import { iconSVG } from './field-icons.js';
import { OPTION_COLORS } from './prop-picker.js';
import { bindFormulaEditor } from './calendar-formula.js';

const OPTION_TYPES = ['select', 'multiselect', 'status'];
const COMPUTED = ['autonumber', 'relation', 'rollup', 'formula'];
const NO_ROLLUP_SOURCE = ['relation', 'rollup', 'formula', 'autonumber', 'files'];
let data = null;
let mgrDirty = false;

function readData() {
    const el = document.getElementById('calPropsData');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); } catch (_) { return null; }
}
function api(path, body) {
    const send = window.csrfFetch || fetch;
    return send(path, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body || {}) })
        .then((r) => r.json().catch(() => ({})).then((j) => {
            if (!r.ok) { const e = new Error(j.message || ('HTTP ' + r.status)); e.status = r.status; e.body = j; throw e; }
            return j;
        }));
}
// 행의 아이콘 단추 (캘린더 툴바 2차 — 글자 단추 ↑↓✕ 대신): 모듈 상수 마크업만
const UI_ICONS = {
    up: '<path d="m6 15 6-6 6 6"/>',
    down: '<path d="m6 9 6 6 6-6"/>',
    trash: '<path d="M4 7h16M10 11v6M14 11v6M5.5 7l1 12a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2l1-12M9 7V4.5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1V7"/>',
};
function actBtn(name, label, cls) {
    const b = el('button', 'calp-propmgr-act ' + cls);
    b.type = 'button';
    b.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + UI_ICONS[name] + '</svg>';
    b.setAttribute('aria-label', label);
    b.title = label;
    return b;
}
function typeLabel(key) { const t = ((data && data.types) || []).find((x) => x.key === key); return t ? t.label : key; }
function el(tag, cls, text) { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; }
function pill(label, color) { return el('span', 'prop-pill ' + (color || 'o8'), label); }
function L(k, fb) { return (data && data.l && data.l[k]) || fb; }
function fail(err) { showToast((err && err.message && err.status && err.status < 500 && err.message) || L('failed', '저장하지 못했습니다'), { error: true }); }

// 유형 변경: 값을 지키는 조합은 바로, 아니면 409 → 확인 뒤 clear_values 로 다시
function changeType(def, to) {
    return api('/calendar/properties/' + def.id + '/update', { type: to }).catch((err) => {
        if (err.status !== 409) throw err;
        return appConfirm({ message: L('type_change_clears', '유형을 바꾸면 기존 값이 무시됩니다. 계속할까요?'), danger: true })
            .then((ok) => { if (!ok) throw new Error('cancel'); return api('/calendar/properties/' + def.id + '/update', { type: to, clear_values: true }); });
    }).then((p) => { Object.assign(def, p); mgrDirty = true; });
}

function targetOptions() {
    const out = [];
    if (data.scope.me) out.push({ value: 'me', label: L('target_me', '내 일정') });
    else out.push({ value: data.scope.calendarId, label: L('target_this', '이 프로젝트') });
    (data.projects || []).forEach((p) => { if (p.id !== data.scope.calendarId) out.push({ value: p.id, label: p.name }); });
    return out;
}
function schemaOfTarget(target) {
    if (target === 'me' || target === data.scope.calendarId) return data.schema;
    const p = (data.projects || []).find((x) => x.id === target);
    return p ? (p.schema || []) : [];
}
function selectEl(options, value, onChange) {
    const s = el('select');
    options.forEach((o) => { const op = el('option', '', o.label); op.value = o.value; s.appendChild(op); });
    s.value = options.some((o) => o.value === value) ? value : ((options[0] && options[0].value) || '');
    s.addEventListener('change', () => onChange(s.value));
    return s;
}
function saveConfig(def, patch) {
    def.config = Object.assign({}, def.config || {}, patch);
    mgrDirty = true;
    return api('/calendar/properties/' + def.id + '/update', { config: def.config }).then((p) => Object.assign(def, p)).catch((e) => { fail(e); throw e; });
}

// 수식: 식 칸 + 함수 참고 + 검사 줄 (속성 spec §14). 저장은 saveConfig 를 쓰지 않는다 —
// 그 경로는 실패를 토스트로 삼키지만, 수식 오류는 검사 줄에 위치와 함께 보여야 한다.
function formulaEditor(def) {
    const box = el('div', 'calp-propmgr-cfg calp-cfg-formula');
    const ta = el('textarea', 'calp-cfg-expr');
    ta.rows = 2; ta.maxLength = 1000; ta.spellcheck = false;
    ['autocomplete', 'autocorrect', 'autocapitalize'].forEach((a) => ta.setAttribute(a, 'off'));
    ta.placeholder = L('formula_placeholder', 'dateBetween(prop("마감"), today(), "days")');
    ta.setAttribute('aria-label', def.name + ' — ' + L('formula_expr', '식'));
    ta.value = def.expr_display || (def.config && def.config.expr) || '';
    ta.disabled = data.canEdit === false;
    const ref = el('button', 'btn btn-ghost btn-sm calp-cfg-fnref', L('formula_fns', '함수') + ' ▾'); ref.type = 'button';
    ref.disabled = ta.disabled;
    const status = el('div', 'calp-cfg-formula-status'); status.setAttribute('aria-live', 'polite');
    box.appendChild(el('span', 'calp-propmgr-label', L('formula_expr', '식')));
    box.appendChild(ta); box.appendChild(ref); box.appendChild(status);
    const f = data.formula || {};
    bindFormulaEditor(ta, {
        status, refButton: ref, labels: data.l, results: f.results, statusKeys: f.statusKeys, fns: f.fns || [],
        props: data.schema.filter((p) => p.id !== def.id).map((p) => p.name).concat((f.builtins || []).map((b) => b.label)),
        check: (expr) => api('/calendar/formula/check', { scope: data.scope.me ? 'me' : data.scope.calendarId, expr, id: def.id }),
        save: (expr) => {
            mgrDirty = true;
            return api('/calendar/properties/' + def.id + '/update', { config: Object.assign({}, def.config || {}, { expr }) })
                .then((p) => Object.assign(def, p));
        },
        initial: def.config && def.config.result ? { type: def.config.result } : null,
    });
    return box;
}

// 유형별 설정 칸 — 관계·롤업·자동 번호·수식만; 나머지는 null
function configEditor(def, rerender) {
    if (def.type === 'formula') return formulaEditor(def);
    const box = el('div', 'calp-propmgr-cfg');
    const cfg = def.config || {};
    if (def.type === 'autonumber') {
        box.appendChild(el('span', 'calp-propmgr-label', L('prefix', '접두')));
        const inp = el('input'); inp.type = 'text'; inp.maxLength = 20; inp.value = cfg.prefix || ''; inp.placeholder = 'PRJ'; inp.className = 'calp-cfg-prefix';
        inp.addEventListener('change', () => saveConfig(def, { prefix: inp.value.trim() }).then(rerender).catch(() => { inp.value = cfg.prefix || ''; }));
        box.appendChild(inp);
        return box;
    }
    if (def.type === 'relation') {
        box.appendChild(el('span', 'calp-propmgr-label', L('target', '대상')));
        const tsel = selectEl(targetOptions(), cfg.target, (v) => saveConfig(def, { target: v }).then(rerender).catch(() => {}));
        tsel.className = 'calp-cfg-target';
        tsel.disabled = !!cfg.pair || !!cfg.reverse; // 짝이 있으면 대상은 고정
        box.appendChild(tsel);
        const single = el('label'); const sc = el('input'); sc.type = 'checkbox'; sc.checked = !!cfg.single; sc.className = 'calp-cfg-single';
        sc.addEventListener('change', () => saveConfig(def, { single: sc.checked }).catch(() => { sc.checked = !sc.checked; }));
        single.appendChild(sc); single.appendChild(document.createTextNode(' ' + L('single', '하나만')));
        box.appendChild(single);
        if (!cfg.reverse) {
            const pair = el('label'); const pc = el('input'); pc.type = 'checkbox'; pc.checked = !!cfg.pair; pc.className = 'calp-prop-pair';
            pc.addEventListener('change', () => {
                mgrDirty = true;
                api('/calendar/properties/' + def.id + '/update', { pair: pc.checked }).then((p) => { Object.assign(def, p); rerender(); })
                    .catch((e) => { pc.checked = !pc.checked; fail(e); });
            });
            pair.appendChild(pc); pair.appendChild(document.createTextNode(' ' + L('pair', '양방향 짝')));
            box.appendChild(pair);
        } else {
            box.appendChild(el('span', 'calp-propmgr-label', L('pair', '양방향 짝') + ' ✓'));
        }
        return box;
    }
    if (def.type === 'rollup') {
        const rels = data.schema.filter((p) => p.type === 'relation').map((p) => ({ value: p.id, label: p.name, target: (p.config && p.config.target) || (data.scope.me ? 'me' : data.scope.calendarId) }));
        if (!data.scope.me) ((data.builtins && data.builtins.relations) || []).forEach((r) => rels.push({ value: r.key, label: r.label, target: data.scope.calendarId }));
        box.appendChild(el('span', 'calp-propmgr-label', L('relation_source', '관계')));
        const rsel = selectEl(rels, cfg.relation, (v) => saveConfig(def, { relation: v, prop: 'title', fn: 'count' }).then(rerender).catch(() => {}));
        rsel.className = 'calp-cfg-relation';
        box.appendChild(rsel);
        const rel = rels.find((r) => r.value === rsel.value);
        const builtinType = (k) => (k === 'start' || k === 'end' ? 'date' : (k === 'done' ? 'check' : (k === 'status' ? 'select' : (k === 'owner' ? 'user' : 'text'))));
        const props = ((data.builtins && data.builtins.props) || []).map((b) => ({ value: b.key, label: b.label, type: builtinType(b.key) }));
        if (rel) schemaOfTarget(rel.target).filter((p) => !NO_ROLLUP_SOURCE.includes(p.type)).forEach((p) => props.push({ value: p.id, label: p.name, type: p.type }));
        box.appendChild(el('span', 'calp-propmgr-label', L('rollup_prop', '속성')));
        const psel = selectEl(props, cfg.prop, (v) => saveConfig(def, { prop: v, fn: 'count' }).then(rerender).catch(() => {}));
        psel.className = 'calp-cfg-prop';
        box.appendChild(psel);
        const ptype = (props.find((p) => p.value === psel.value) || {}).type || 'text';
        const allowed = (fn) => ({ count: 1, count_values: 1, count_empty: 1, show: 1 })[fn]
            || (fn === 'percent_done' && (ptype === 'check' || ptype === 'status' || ptype === 'select'))
            || (['sum', 'avg', 'min', 'max'].includes(fn) && ptype === 'number')
            || (['earliest', 'latest'].includes(fn) && ptype === 'date')
            || (fn === 'unique' && ['select', 'multiselect', 'users', 'user', 'status', 'text'].includes(ptype));
        box.appendChild(el('span', 'calp-propmgr-label', L('rollup_fn', '계산')));
        const fsel = selectEl((data.fns || []).filter((f) => allowed(f.key)).map((f) => ({ value: f.key, label: f.label })), cfg.fn, (v) => saveConfig(def, { fn: v }).then(rerender).catch(() => {}));
        fsel.className = 'calp-cfg-fn';
        box.appendChild(fsel);
        return box;
    }
    return null;
}

function optionsEditor(def) {
    const opts = el('div', 'calp-propmgr-opts');
    const T = document.getElementById('calpPropMgr').dataset;
    opts.appendChild(el('span', 'calp-propmgr-label', T.lOptions || '옵션'));
    const save = () => { mgrDirty = true; api('/calendar/properties/' + def.id + '/update', { options: def.options || [] }).then((p) => { Object.assign(def, p); renderMgr(); }).catch(fail); };
    (def.options || []).forEach((o, oi) => {
        const p = pill(o.label, o.color); p.title = L('option_color', '누르면 색이 바뀝니다'); p.classList.add('calp-opt-pill');
        p.addEventListener('click', () => { o.color = OPTION_COLORS[(OPTION_COLORS.indexOf(o.color) + 1) % OPTION_COLORS.length]; save(); });
        if (def.type === 'status') {
            const g = selectEl((data.groups || []).map((x) => ({ value: x.key, label: x.label })), o.group || 'todo', (v) => { o.group = v; save(); });
            g.className = 'calp-prop-group'; g.addEventListener('click', (e) => e.stopPropagation());
            p.appendChild(g);
        }
        const rm = el('button', 'calp-opt-rm', '✕'); rm.type = 'button'; rm.setAttribute('aria-label', L('remove_option', '옵션 빼기') + ': ' + o.label); rm.title = L('remove_option', '옵션 빼기');
        rm.addEventListener('click', (e) => { e.stopPropagation(); def.options.splice(oi, 1); save(); });
        p.appendChild(rm);
        opts.appendChild(p);
    });
    const add = el('input'); add.type = 'text'; add.className = 'calp-opt-add'; add.placeholder = T.lAddOption || '옵션 추가 후 Enter'; add.maxLength = 60; add.setAttribute('aria-label', T.lAddOption || '옵션 추가 후 Enter');
    add.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        const v = add.value.trim();
        if (!v || (def.options || []).some((o) => o.label === v)) return;
        const next = { label: v, color: OPTION_COLORS[(def.options || []).length % OPTION_COLORS.length] };
        if (def.type === 'status') next.group = 'todo';
        def.options = (def.options || []).concat([next]);
        save();
    });
    opts.appendChild(add);
    return opts;
}

function renderMgr() {
    const mgr = document.getElementById('calpPropMgr');
    const list = document.getElementById('calpPropMgrList');
    if (!mgr || !list || !data) return;
    list.innerHTML = '';
    const T = mgr.dataset;
    const schema = data.schema;
    const canEdit = data.canEdit !== false;
    schema.forEach((def, idx) => {
        const row = el('div', 'calp-propmgr-row'); row.dataset.id = def.id; row.dataset.type = def.type;
        // 머리 한 줄: 유형 아이콘 · 이름 · 유형 칩 · (hover·포커스에) 옮기기 ↑↓ · 삭제
        const head = el('div', 'calp-propmgr-rowhead');
        const ticon = el('span', 'calp-propmgr-ticon'); ticon.innerHTML = iconSVG(def.type); ticon.title = typeLabel(def.type); ticon.setAttribute('aria-hidden', 'true');
        const name = el('input'); name.type = 'text'; name.className = 'form-input calp-propmgr-name'; name.value = def.name; name.maxLength = 60; name.disabled = !canEdit; name.setAttribute('aria-label', L('prop_name', '속성 이름'));
        name.addEventListener('change', () => { const v = name.value.trim(); if (!v) { name.value = def.name; return; } def.name = v; mgrDirty = true; api('/calendar/properties/' + def.id + '/update', { name: v }).catch(fail); });
        const type = el('select');
        // 계산 유형(자동 번호·관계·롤업)은 서로만, 값 유형은 값 유형끼리만 바꾼다 (spec §9)
        const computed = COMPUTED.includes(def.type);
        (data.types || []).filter((t) => t.key === def.type || COMPUTED.includes(t.key) === computed).forEach((t) => { const o = el('option', '', t.label); o.value = t.key; type.appendChild(o); });
        type.value = def.type; type.setAttribute('data-inapp-select', ''); type.disabled = !canEdit || computed || !!(def.config && def.config.reverse);
        type.setAttribute('aria-label', L('prop_type', '속성 유형')); setOptionIcons(type, iconSVG);
        type.addEventListener('change', () => {
            changeType(def, type.value).then(renderMgr).catch((e) => { if (e.message !== 'cancel') fail(e); type.value = def.type; type.dispatchEvent(new Event('inapp-sync')); });
        });
        const up = actBtn('up', L('move_up', '위로 옮기기'), 'calp-propmgr-up'); up.disabled = idx === 0 || !canEdit;
        const down = actBtn('down', L('move_down', '아래로 옮기기'), 'calp-propmgr-down'); down.disabled = idx === schema.length - 1 || !canEdit;
        const reorder = (dir) => {
            const other = schema[idx + dir]; const a = def.sort_order, b = other.sort_order;
            def.sort_order = b === a ? a + dir : b; other.sort_order = a === b ? b - dir : a;
            schema.sort((x, y) => x.sort_order - y.sort_order); mgrDirty = true;
            Promise.all([api('/calendar/properties/' + def.id + '/update', { sort_order: def.sort_order }), api('/calendar/properties/' + other.id + '/update', { sort_order: other.sort_order })]).then(renderMgr).catch(fail);
        };
        up.addEventListener('click', () => reorder(-1)); down.addEventListener('click', () => reorder(1));
        const del = actBtn('trash', L('delete_prop', '속성 삭제') + ': ' + def.name, 'calp-propmgr-del'); del.disabled = !canEdit;
        del.addEventListener('click', () => {
            // 이 속성을 쓰는 수식을 이름으로 알린다 — 지우면 그 수식은 ⚠ 가 된다 (수식 D12)
            const users = schema.filter((q) => q.type === 'formula' && q.id !== def.id && ((q.config && q.config.expr) || '').includes('"@' + def.id + '"')).map((q) => q.name);
            const detail = users.length ? L('formula_ref_warn', '이 속성을 쓰는 수식이 ⚠ 로 바뀝니다: {names}').replace('{names}', users.join(', ')) : '';
            appConfirm({ message: T.lDeleteConfirm, detail, danger: true }).then((ok) => {
                if (!ok) return;
                api('/calendar/properties/' + def.id + '/delete', {}).then(() => { data.schema = schema.filter((p) => p.id !== def.id); mgrDirty = true; renderMgr(); }).catch(fail);
            });
        });
        const acts = el('span', 'calp-propmgr-acts'); acts.appendChild(up); acts.appendChild(down); acts.appendChild(del);
        head.appendChild(ticon); head.appendChild(name); head.appendChild(type); head.appendChild(acts);
        row.appendChild(head);
        if (OPTION_TYPES.includes(def.type)) row.appendChild(optionsEditor(def));
        const cfg = configEditor(def, renderMgr); if (cfg) row.appendChild(cfg);
        const show = el('label', 'calp-propmgr-show'); const cb = el('input'); cb.type = 'checkbox'; cb.className = 'calp-propmgr-showcb'; cb.checked = def.show_in_list !== false; cb.disabled = !canEdit;
        cb.addEventListener('change', () => { def.show_in_list = cb.checked; mgrDirty = true; api('/calendar/properties/' + def.id + '/update', { show_in_list: cb.checked }).catch(fail); });
        show.appendChild(cb); show.appendChild(document.createTextNode(' ' + T.lShow));
        if (T.lShowHint) show.title = T.lShowHint; // 「표 열로 추가」: 켜면 표 뷰마다 열 (AQ10)
        row.appendChild(show);
        list.appendChild(row);
    });
    initInappSelects(list);
    const addForm = document.getElementById('calpPropMgrAdd');
    if (addForm) {
        addForm.hidden = !canEdit;
        setOptionIcons(addForm.elements.type, iconSVG);
        initInappSelects(addForm);
    }
    renderLegacy(mgr);
    // 오버레이: 범위 전환 줄 — 내 속성 · 보이는 프로젝트(고칠 수 있으면 허브와 같은 편집, 아니면 읽기 전용) (AY3)
    const projBox = document.getElementById('calpPropMgrProjects');
    if (projBox) {
        projBox.innerHTML = '';
        const projs = (data.projects || []).filter((p) => p.canEdit || (p.schema || []).length);
        if (projs.length) {
            const bar = el('div', 'calp-propmgr-scopes'); bar.setAttribute('role', 'group');
            const cur = data.scope.calendarId || '';
            const tab = (id, label) => {
                const b = el('button', 'calp-propmgr-scope', label); b.type = 'button'; b.dataset.scope = id;
                b.setAttribute('aria-pressed', cur === id ? 'true' : 'false');
                b.addEventListener('click', () => { if (cur !== id) switchScope(id); });
                return b;
            };
            bar.appendChild(tab('', L('my_props', '커스텀 속성')));
            projs.forEach((p) => bar.appendChild(tab(p.id, p.name)));
            projBox.appendChild(bar);
            const p = cur ? projOf(cur) : null;
            if (p) {
                const note = el('div', 'calp-propmgr-scopenote text-muted', (p.canEdit ? L('project_props_desc', '이 프로젝트의 모든 작업이 공유하는 열입니다') : L('readonly', '읽기 전용')) + ' ');
                const a = el('a', 'calp-propmgr-projlink', L('open_hub', '허브 열기 →')); a.href = '/calendar/projects/' + encodeURIComponent(p.id);
                note.appendChild(a); projBox.appendChild(note);
            }
        }
    }
}
// ── 옛 자유 속성 → 내 속성 (AY6): 내 속성 범위에서만, 미리보기(이름·유형 묶음) → 고른 것만 옮긴다 ──
let legacySeq = 0;
function renderLegacy(mgr) {
    let box = document.getElementById('calpPropMgrLegacy');
    const list = document.getElementById('calpPropMgrList');
    if (!data.scope.me || data.canEdit === false || !list) { if (box) box.remove(); return; }
    if (!box) { box = el('div', 'calp-propmgr-legacy'); box.id = 'calpPropMgrLegacy'; box.hidden = true; list.insertAdjacentElement('afterend', box); }
    const my = ++legacySeq;
    fetch('/calendar/me/properties/legacy', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : { groups: [] }))
        .then((j) => { if (my === legacySeq && data.scope.me) paintLegacy(box, (j && j.groups) || []); })
        .catch(() => { box.hidden = true; });
    void mgr;
}
function paintLegacy(box, groups) {
    box.innerHTML = '';
    box.hidden = !groups.length;
    if (!groups.length) return;
    const typeName = (t) => { const x = (data.types || []).find((o) => o.key === t); return x ? x.label : t; };
    box.appendChild(el('div', 'calp-propmgr-legacy-title', L('legacy_title', '옛 자유 속성')));
    box.appendChild(el('p', 'calp-propmgr-legacy-desc text-muted', L('legacy_desc', '')));
    const ul = el('div', 'calp-propmgr-legacy-list');
    groups.forEach((g) => {
        const lab = el('label', 'calp-propmgr-legacy-row' + (g.conflict ? ' is-conflict' : ''));
        const cb = el('input'); cb.type = 'checkbox'; cb.checked = !g.conflict; cb.disabled = !!g.conflict;
        cb.dataset.name = g.name; cb.dataset.type = g.type; cb.dataset.events = String(g.events || 0);
        lab.appendChild(cb);
        lab.appendChild(el('b', '', g.name));
        lab.appendChild(el('span', 'prop-pill neutral', typeName(g.type)));
        lab.appendChild(el('span', 'text-muted', L('legacy_events', '일정 %d개').replace('%d', String(g.events || 0))));
        if (g.conflict) lab.appendChild(el('span', 'calp-propmgr-legacy-note', L('legacy_conflict', '')));
        else lab.appendChild(el('span', 'calp-propmgr-legacy-note', g.existing ? L('legacy_merge', '기존 속성에 합침') : L('legacy_new', '새 내 속성')));
        if ((g.samples || []).length) lab.appendChild(el('span', 'calp-propmgr-legacy-samples text-muted', g.samples.join(' · ')));
        ul.appendChild(lab);
    });
    box.appendChild(ul);
    const go = el('button', 'btn btn-secondary btn-sm', L('legacy_promote', '골라 옮기기')); go.type = 'button'; go.id = 'calpPropMgrLegacyGo';
    const sync = () => { go.disabled = !ul.querySelector('input:checked'); };
    ul.addEventListener('change', sync); sync();
    go.addEventListener('click', () => {
        const picked = Array.from(ul.querySelectorAll('input:checked'));
        if (!picked.length) return;
        const events = picked.reduce((n, c) => n + (Number(c.dataset.events) || 0), 0);
        appConfirm({ message: L('legacy_confirm', '속성 %d개를 내 속성으로 옮길까요? 일정 %d개의 값이 옮겨집니다.').replace('%d', String(picked.length)).replace('%d', String(events)) }).then((ok) => {
            if (!ok) return;
            go.disabled = true;
            api('/calendar/me/properties/legacy/promote', { groups: picked.map((c) => ({ name: c.dataset.name, type: c.dataset.type })) })
                .then((res) => {
                    mgrDirty = true; // 새 열 — 닫을 때 서버 렌더로 맞춘다
                    let msg = L('legacy_done', '속성 %d개를 옮겼습니다 — 일정 %d개').replace('%d', String((res.created || 0) + (res.merged || 0))).replace('%d', String(res.events || 0));
                    if (res.kept) msg += ' · ' + L('legacy_kept', '').replace('%d', String(res.kept));
                    showToast(msg);
                    return fetch('/calendar/me/properties', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null));
                })
                .then((list) => { if (Array.isArray(list) && data.scope.me) { data.schema = list; renderMgr(); } })
                .catch((e) => { go.disabled = false; fail(e); });
        });
    });
    box.appendChild(go);
}

// ── 범위 전환 (AY3): 오버레이의 관리자가 고르는 스키마 — 섬의 내 속성(home)과 프로젝트들 사이를 오간다.
// 떠나는 범위의 목록을 먼저 되돌려 적는다(삭제는 배열을 새로 만든다).
let home = null;
function projOf(id) { return ((data && data.projects) || []).find((p) => p.id === id) || null; }
function switchScope(id) {
    if (!home) home = { scope: data.scope, schema: data.schema, canEdit: data.canEdit };
    if (data.scope.calendarId) { const was = projOf(data.scope.calendarId); if (was) was.schema = data.schema; } else home.schema = data.schema;
    const p = id ? projOf(id) : null;
    if (p) { data.scope = { calendarId: p.id, me: false, url: p.url }; data.schema = p.schema || []; data.canEdit = !!p.canEdit; } else { data.scope = home.scope; data.schema = home.schema; data.canEdit = home.canEdit; }
    renderMgr();
}
function closeMgr() {
    const mgr = document.getElementById('calpPropMgr'); if (!mgr) return;
    mgr.hidden = true;
    if (mgrDirty) window.location.reload(); // 열 구성이 바뀌었으니 서버 렌더로 맞춘다
}
function openMgr() {
    const mgr = document.getElementById('calpPropMgr'); if (!mgr) return;
    if (!mgr.hidden) { closeMgr(); return; }
    data = readData(); if (!data) return;
    home = null;
    renderMgr();
    mgr.hidden = false;
}

export function setupPropManager() {
    if (document.documentElement.dataset.calPropsBound) return;
    document.documentElement.dataset.calPropsBound = '1';
    document.addEventListener('click', (e) => {
        if (e.target.closest('#calpPropsBtn')) { e.preventDefault(); openMgr(); return; }
        if (e.target.closest('#calpPropMgrClose, #calpPropMgrDone')) closeMgr();
    });
    document.addEventListener('submit', (e) => {
        const form = e.target.closest('#calpPropMgrAdd'); if (!form) return;
        e.preventDefault();
        data = data || readData(); if (!data) return;
        const name = form.elements.name.value.trim(); if (!name) return;
        const type = form.elements.type.value;
        const body = { name, type };
        // 관계는 대상, 롤업은 관계·속성·함수가 있어야 만들어진다 — 기본값을 채워 만든 뒤 행에서 고친다
        if (type === 'relation') body.config = { target: data.scope.me ? 'me' : data.scope.calendarId };
        if (type === 'rollup') {
            const rel = data.schema.find((p) => p.type === 'relation');
            body.config = { relation: rel ? rel.id : (data.scope.me ? '' : 'children'), prop: 'title', fn: 'count' };
        }
        api(data.scope.url, body)
            .then((p) => {
                data.schema.push(p); mgrDirty = true; form.reset(); const t = form.elements.type; t.value = 'text'; t.dispatchEvent(new Event('inapp-sync')); renderMgr();
                // 새 수식은 빈 식으로 시작한다 — 바로 칠 수 있게 그 행의 식 칸으로 (수식 D11)
                if (p.type === 'formula') { const ta = document.querySelector('#calpPropMgrList .calp-propmgr-row[data-id="' + p.id + '"] .calp-cfg-expr'); if (ta) ta.focus(); }
            })
            .catch(fail);
    });
    document.addEventListener('keydown', (e) => {
        const mgr = document.getElementById('calpPropMgr');
        if (e.key === 'Escape' && mgr && !mgr.hidden) { closeMgr(); e.stopImmediatePropagation(); }
    }, true);
    const addForm = document.getElementById('calpPropMgrAdd'); if (addForm) initInappSelects(addForm);
}
